import type { ProductForSelector } from "@/shared/models/WorkOrder";

type FieldConfig = {
	field: keyof ProductForSelector;
	label: string;
	/** Custom value formatter. If absent, uses raw value */
	format?: (product: ProductForSelector) => string;
	/** When true, this field also appears as an AG Grid column in the table */
	showInTable?: boolean;
	/** Shade the field green so the value reads as the goal (target). */
	highlight?: boolean;
	/** Also show the value as tape-measure 1/32" fraction. */
	fraction?: boolean;
};

export type AssetTypeConfig = {
	/** Read-only fields from product/specs shown after product selection */
	productFields: FieldConfig[];
	/** Which BundleCount-like field to use for TotalBundles calculation */
	bundleCountField: keyof ProductForSelector;
	/** Label for the bundle count display */
	bundleCountLabel: string;
	/** Offer the next-location picker (options come from FAB.GetNextLocationAssets + Ship) */
	showNextLocation?: boolean;
	/** Orders reference a clip (FAB.catClips) instead of a product */
	usesClip?: boolean;
};

// Asset type keys resolved from ParentAssetId or AssetId
export type AssetType = "weld" | "saw" | "wax" | "paint" | "punch" | "clip";

export type AreaIds = {
	welds: number;
	saws: number;
	wax: number;
	paint: number;
	punch: number;
	clips: number;
};

/** Resolve asset type from assetId + parentAssetId using area IDs from settings */
export const resolveAssetType = (
	assetId: number,
	parentAssetId: number | null,
	areaIds: AreaIds,
): AssetType => {
	// Standalone assets (both area + asset)
	if (assetId === areaIds.wax) return "wax";
	if (assetId === areaIds.paint) return "paint";
	if (assetId === areaIds.punch) return "punch";
	// Children of areas
	if (parentAssetId === areaIds.welds) return "weld";
	if (parentAssetId === areaIds.saws) return "saw";
	if (parentAssetId === areaIds.clips) return "clip";
	// Default
	return "weld";
};

export const getAreaAssetIdForAssetType = (
	assetType: AssetType,
	areaIds: AreaIds,
): number => {
	switch (assetType) {
		case "saw":
			return areaIds.saws;
		case "wax":
			return areaIds.wax;
		case "paint":
			return areaIds.paint;
		case "punch":
			return areaIds.punch;
		case "clip":
			return areaIds.clips;
		case "weld":
		default:
			return areaIds.welds;
	}
};

const COMMON_PRODUCT_FIELDS: FieldConfig[] = [
	{ field: "CustomerName", label: "Customer" },
	{ field: "SectionName", label: "Section" },
	{ field: "GradeName", label: "Grade" },
];

export const ASSET_TYPE_CONFIGS: Record<AssetType, AssetTypeConfig> = {
	weld: {
		productFields: [
			...COMMON_PRODUCT_FIELDS,
			{ field: "CutLength", label: "Cut Length", fraction: true },
			{ field: "FinishedLength", label: "Finished Length", fraction: true },
			{ field: "Tolerance", label: "Tolerance" },
			{ field: "WebHoles", label: "Web Holes" },
			{ field: "Program", label: "Program", showInTable: true },
			{ field: "CleaningCycle", label: "Cleaning Cycle" },
			{
				field: "ClipWidth",
				label: "Clip Size",
				format: (p) =>
					p.ClipWidth != null || p.ClipHeight != null
						? `${p.ClipWidth} x ${p.ClipHeight}`
						: "",
			},
			{ field: "HolePattern", label: "Hole Pattern" },
			{ field: "HoleGage", label: "Hole Gage" },
			{ field: "SideGage", label: "Side Gage" },
			{ field: "EntryKeeper", label: "Entry Keeper" },
			{ field: "ExitKeeper", label: "Exit Keeper" },
			{ field: "BottomKeeper", label: "Bottom Keeper" },
			{ field: "StickSize", label: "Stick Size" },
			{ field: "GroovedTimber", label: "Timber Size" },
		],

		bundleCountField: "BundleCount",
		bundleCountLabel: "Pcs/Bundle",
	},
	saw: {
		productFields: [
			...COMMON_PRODUCT_FIELDS,
			{ field: "BottomTolerance", label: "Bottom", fraction: true },
			{
				field: "TargetLength",
				label: "Target",
				showInTable: true,
				highlight: true,
				fraction: true,
			},
			{ field: "TopTolerance", label: "Top", fraction: true },
			{ field: "MillLength", label: "Mill Length", showInTable: true },
			{ field: "MillPcB", label: "Mill Pc.B" },
			{ field: "FinishPcB", label: "Finish Pc.B" },
		],

		bundleCountField: "FinishPcB",
		bundleCountLabel: "Finish Pc.B",
		showNextLocation: true,
	},
	wax: {
		productFields: [
			...COMMON_PRODUCT_FIELDS,
			{
				field: "FinishedLength",
				label: "Finished Length",
				showInTable: true,
				fraction: true,
			},
			{ field: "WebHoles", label: "Web Holes", showInTable: true },
			{
				field: "ClipWidth",
				label: "Clip Size",
				format: (p) =>
					p.ClipWidth != null || p.ClipHeight != null
						? `${p.ClipWidth} x ${p.ClipHeight}`
						: "",
			},
			{ field: "HolePattern", label: "Hole Pattern" },
		],

		bundleCountField: "BundleCount",
		bundleCountLabel: "Pcs/Bundle",
	},
	paint: {
		productFields: [
			...COMMON_PRODUCT_FIELDS,
			{
				field: "FinishedLength",
				label: "Finished Length",
				showInTable: true,
				fraction: true,
			},
			{ field: "WebHoles", label: "Web Holes" },
			{ field: "PaintCutType", label: "Sq. Cut / Or Clip", showInTable: true },
			{ field: "SpecialInstructions", label: "Special Instructions" },
		],

		bundleCountField: "BundleCount",
		bundleCountLabel: "Pcs/Bundle",
	},
	punch: {
		productFields: [
			...COMMON_PRODUCT_FIELDS,
			{
				field: "CutLength",
				label: "Cut Length",
				showInTable: true,
				fraction: true,
			},
			{
				field: "BandUp",
				label: "Band Up",
				showInTable: true,
				format: (p) => (p.BandUp ? "Yes" : p.BandUp === false ? "No" : ""),
			},
		],

		bundleCountField: "BundleCount",
		bundleCountLabel: "Pcs/Bundle",
	},
	clip: {
		productFields: [],
		bundleCountField: "BundleCount",
		bundleCountLabel: "Pcs/Bundle",
		usesClip: true,
	},
};

/** Return the subset of productFields flagged for table display */
export const getTableProductFields = (assetType: AssetType) =>
	ASSET_TYPE_CONFIGS[assetType].productFields.filter((f) => f.showInTable);

/** SAW calculated fields from product + quantity */
export const calcSawFields = (
	product: Pick<
		ProductForSelector,
		"TargetLength" | "MillLength" | "MillPcB" | "FinishPcB"
	>,
	quantity: number | null,
) => {
	const { TargetLength, MillLength, MillPcB, FinishPcB } = product;

	if (!TargetLength || !MillLength || !MillPcB || TargetLength === 0)
		return null;

	// MillLength is in feet, TargetLength is in inches → convert ft to in
	const cutsPerBundle = Math.floor((MillLength * 12) / TargetLength);
	const pcsPerMillBundle = MillPcB * cutsPerBundle;

	if (pcsPerMillBundle === 0) return null;

	const numMLB =
		quantity && quantity > 0 ? Math.ceil(quantity / pcsPerMillBundle) : null;
	const numTags =
		numMLB && FinishPcB && FinishPcB > 0
			? Math.ceil((numMLB * pcsPerMillBundle) / FinishPcB)
			: null;
	const overcut =
		numMLB && quantity ? numMLB * pcsPerMillBundle - quantity : null;

	return { cutsPerBundle, pcsPerMillBundle, numMLB, numTags, overcut };
};
